import { useState, useCallback } from "react";
import { validateForm } from "../../../utils/fieldValidators";
import { Form } from "../FormBuilder.types";

export interface UseFormValidationOptions {
  form?: Form;
  externalValidationErrors?: Record<string, boolean>;
  onValidationErrorsChange?: (errors: Record<string, boolean>) => void;
}

export const useFormValidation = (options: UseFormValidationOptions = {}) => {
  const { form, externalValidationErrors, onValidationErrorsChange } = options;

  const [internalValidationErrors, setInternalValidationErrors] = useState<
    Record<string, boolean>
  >({});

  const validationErrors = externalValidationErrors ?? internalValidationErrors;
  const setValidationErrors = onValidationErrorsChange ?? setInternalValidationErrors;

  const validateFields = useCallback(
    (formElement: HTMLFormElement): boolean => {
      const formData = new FormData(formElement);
      
      if (!form) {
        return true;
      }

      const errors = validateForm(form.elements, formData);
      setValidationErrors(errors);

      const isValid = Object.keys(errors).length === 0;
      return isValid;
    },
    [form, setValidationErrors]
  );

  const handleFieldChange = useCallback(
    (fieldName: string) => {
      if (validationErrors[fieldName]) {
        const updater = (prev: Record<string, boolean>) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        };
        setValidationErrors(updater as any);
      }
    },
    [validationErrors, setValidationErrors]
  );

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, [setValidationErrors]);

  return {
    validationErrors,
    validateFields,
    handleFieldChange,
    clearValidationErrors,
  };
};
